import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Loader2, Plus } from 'lucide-react';
import api from '../lib/api';
import { Pill, money, when } from './PlatformConsole';

/** Operator-grade confirmation: typed word + mandatory reason for anything
 *  destructive or financial. */
export const ConfirmAction = ({ open, title, description, confirmWord, requireReason = true,
                                busy, onCancel, onConfirm, children, testId = 'pc-confirm', confirmLabel = 'Confirm' }) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) { setTyped(''); setReason(''); } }, [open]);
  if (!open) return null;
  const ready = (!confirmWord || typed.trim().toUpperCase() === confirmWord.toUpperCase())
    && (!requireReason || reason.trim().length >= 3);
  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="pc-modal" data-testid={testId}>
        <h3>{title}</h3>
        <p className="pc-sub" style={{ marginBottom: '.9rem' }}>{description}</p>
        {children}
        {requireReason && (
          <div style={{ marginTop: '.7rem' }}>
            <label className="pc-field">Reason (recorded in the audit trail)</label>
            <textarea className="pc-textarea" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)} data-testid={`${testId}-reason`} />
          </div>
        )}
        {confirmWord && (
          <div style={{ marginTop: '.7rem' }}>
            <label className="pc-field">Type “{confirmWord}” to confirm</label>
            <input className="pc-input" value={typed} onChange={(e) => setTyped(e.target.value)}
              data-testid={`${testId}-word`} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" className="pc-btn" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</button>
          <button type="button" className="pc-btn pc-btn-primary" disabled={!ready || busy}
            onClick={() => onConfirm(reason.trim())} data-testid={`${testId}-submit`}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlatformPharmacies = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', town: '', phone: '', email: '', trial_days: 14 });
  const search = params.get('search') || '';
  const status = params.get('status') || '';

  const load = useCallback(() => {
    api.get('/platform/pharmacies', { params: { search, status } })
      .then(r => setRows(r.data.pharmacies || []))
      .catch(() => toast.error('Could not load pharmacies'));
  }, [search, status]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      const { data } = await api.post('/platform/pharmacies', form);
      toast.success('Pharmacy created on trial');
      setCreating(false);
      navigate(`/platform/pharmacies/${data.tenant_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  return (
    <div data-testid="pc-pharmacies">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Pharmacies</h1>
          <p className="pc-sub">{rows ? `${rows.length} shown` : 'loading…'}{search && ` · matching “${search}”`}</p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <select className="pc-select" style={{ width: 150 }} value={status}
            onChange={(e) => setParams({ ...(search ? { search } : {}), ...(e.target.value ? { status: e.target.value } : {}) })}
            data-testid="pc-status-filter">
            <option value="">All statuses</option>
            {['trial', 'active', 'past_due', 'suspended'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <button className="pc-btn pc-btn-primary" onClick={() => setCreating(true)} data-testid="pc-new-pharmacy">
            <Plus className="h-3.5 w-3.5 inline mr-1" /> New pharmacy
          </button>
        </div>
      </div>
      <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
        <table className="pc-table">
          <thead><tr>
            <th>Pharmacy</th><th>Town</th><th>Status</th><th>KYC</th><th>Plan</th>
            <th className="pc-num">Branches</th><th className="pc-num">Users</th><th>Renews</th><th>Last sale</th>
          </tr></thead>
          <tbody>
            {(rows || []).map(r => (
              <tr key={r.tenant_id} onClick={() => navigate(`/platform/pharmacies/${r.tenant_id}`)}
                data-testid={`pc-pharmacy-row-${r.tenant_id}`}>
                <td><b style={{ color: '#fff' }}>{r.name}</b><div className="pc-mono">{r.tenant_id}</div></td>
                <td>{r.town || '—'}</td>
                <td><Pill value={r.status} /></td>
                <td><Pill value={r.kyc_status} /></td>
                <td style={{ textTransform: 'capitalize' }}>{r.plan}{r.cycle ? ` · ${r.cycle}` : ''}</td>
                <td className="pc-num">{r.branch_count}</td>
                <td className="pc-num">{r.user_count}</td>
                <td>{when(r.period_end)}</td>
                <td>{when(r.last_activity)}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={9} className="pc-muted" style={{ padding: '2rem', textAlign: 'center' }} data-testid="pc-no-pharmacies">
                No pharmacies match.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmAction
        open={creating} title="Create a pharmacy" description="Starts on trial, pending KYC."
        requireReason={false} onCancel={() => setCreating(false)} onConfirm={create} testId="pc-create-pharmacy"
      >
        <div className="pc-row">
          {[['name', 'Pharmacy name'], ['town', 'Town'], ['phone', 'Phone'], ['email', 'Owner email']].map(([k, label]) => (
            <div key={k}>
              <label className="pc-field">{label}</label>
              <input className="pc-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`pc-create-${k}`} />
            </div>
          ))}
          <div>
            <label className="pc-field">Trial days</label>
            <input className="pc-input" type="number" min={0} value={form.trial_days}
              onChange={(e) => setForm({ ...form, trial_days: Number(e.target.value) || 0 })} data-testid="pc-create-trial" />
          </div>
        </div>
      </ConfirmAction>
    </div>
  );
};

const AllowanceRow = ({ a, onGrant, onToggle, onAdjust, onTopUp }) => (
  <tr data-testid={`pc-allowance-${a.kind}`}>
    <td>{a.label}{a.disabled && <span className="pc-pill pc-pill-suspended" style={{ marginLeft: 6 }}>off</span>}</td>
    <td className="pc-num">{a.used.toLocaleString()} / {a.included.toLocaleString()}</td>
    <td className="pc-num">{a.credits.toLocaleString()}</td>
    <td className="pc-num">{a.remaining.toLocaleString()}</td>
    <td className="pc-num">{a.projected_month.toLocaleString()}</td>
    <td style={{ minWidth: 110 }}>
      <div className={`pc-bar ${a.exhausted ? 'pc-bar-out' : a.pct_used >= 80 ? 'pc-bar-hot' : ''}`}>
        <i style={{ width: `${Math.min(100, a.pct_used)}%` }} />
      </div>
    </td>
    <td className="pc-num">
      <button className="pc-btn" onClick={() => onGrant(a)} data-testid={`pc-grant-${a.kind}`}>Grant</button>{' '}
      <button className="pc-btn" onClick={() => onAdjust(a)} data-testid={`pc-adjust-${a.kind}`}>Adjust</button>{' '}
      <button className="pc-btn" onClick={() => onTopUp(a)} data-testid={`pc-topup-${a.kind}`}>Top-up link</button>{' '}
      <button className="pc-btn" onClick={() => onToggle(a)} data-testid={`pc-toggle-${a.kind}`}>
        {a.disabled ? 'Enable' : 'Disable'}
      </button>
    </td>
  </tr>
);

export const PlatformPharmacyDetail = () => {
  const { tenantId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // {type, payload}
  const [draft, setDraft] = useState({});
  const [topUpLink, setTopUp] = useState(null);

  const load = useCallback(() => {
    api.get(`/platform/pharmacies/${tenantId}`).then(r => setData(r.data))
      .catch(() => toast.error('Could not load pharmacy'));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); setDialog(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const startSupportView = async (reason) => {
    setBusy(true);
    try {
      const { data: result } = await api.post(`/platform/pharmacies/${tenantId}/impersonate`, { reason });
      toast.success(`Opening read-only support view for ${result.pharmacy_name}`);
      localStorage.removeItem('pharmxai_user');
      window.location.assign(result.redirect || '/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open support view');
      setBusy(false);
    }
  };

  if (!data) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const p = data.pharmacy;
  const sub = data.subscription || {};
  const open = (type, seed = {}) => { setDraft(seed); setDialog(type); };

  const dialogs = {
    suspend: { title: `Suspend ${p.name}?`, word: 'SUSPEND',
      description: 'They keep their data and their billing screens but cannot sell or edit anything.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/status`, { status: 'suspended', reason }), 'Pharmacy suspended') },
    restore: { title: `Restore ${p.name}?`, word: '', requireReason: true,
      description: 'Returns the pharmacy to trial so it can transact again.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/status`, { status: 'trial', reason }), 'Pharmacy restored') },
    golive: { title: 'Move to active (paid)?', word: '',
      description: 'Only allowed once pharmacy KYC is approved.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/status`, { status: 'active', reason }), 'Pharmacy is live') },
    plan: { title: 'Change plan', word: '',
      description: 'Sets the plan and resets the billing period from today.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/plan`,
        { tier: draft.tier || 'essentials', cycle: draft.cycle || 'monthly', reason }), 'Plan updated'),
      body: (
        <div className="pc-row">
          <div><label className="pc-field">Plan</label>
            <select className="pc-select" value={draft.tier || 'essentials'}
              onChange={(e) => setDraft(d => ({ ...d, tier: e.target.value }))} data-testid="pc-plan-tier">
              <option value="essentials">Essentials — GHS 350/mo</option>
              <option value="professional">Professional — GHS 1,200/mo</option>
            </select></div>
          <div><label className="pc-field">Cycle</label>
            <select className="pc-select" value={draft.cycle || 'monthly'}
              onChange={(e) => setDraft(d => ({ ...d, cycle: e.target.value }))} data-testid="pc-plan-cycle">
              <option value="monthly">Monthly</option>
              <option value="annual">Annual (−20%)</option>
            </select></div>
        </div>
      ) },
    trial: { title: 'Start or extend trial', word: '', description: 'Adds days from today, or from the current trial end.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/trial`,
        { days: Number(draft.days) || 14, reason }), 'Trial updated'),
      body: (<div><label className="pc-field">Days</label>
        <input className="pc-input" type="number" min={1} value={draft.days ?? 14}
          onChange={(e) => setDraft(d => ({ ...d, days: e.target.value }))} data-testid="pc-trial-days" /></div>) },
    cancel: { title: 'Cancel at period end?', word: 'CANCEL',
      description: 'They keep access until the current period ends.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/cancel`, { reason }), 'Cancellation scheduled') },
    payment: { title: 'Record an out-of-band payment', word: '',
      description: 'A bookkeeping entry only — nothing is charged to a card.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/record-payment`, {
        amount_ghs: Number(draft.amount) || 0, method: draft.method || 'bank_transfer',
        reference: draft.reference || '', reason }), 'Payment recorded'),
      body: (
        <div className="pc-row">
          <div><label className="pc-field">Amount (GHS)</label>
            <input className="pc-input" type="number" min={1} step="0.01" value={draft.amount || ''}
              onChange={(e) => setDraft(d => ({ ...d, amount: e.target.value }))} data-testid="pc-payment-amount" /></div>
          <div><label className="pc-field">Method</label>
            <select className="pc-select" value={draft.method || 'bank_transfer'}
              onChange={(e) => setDraft(d => ({ ...d, method: e.target.value }))} data-testid="pc-payment-method">
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
              <option value="mobile_money">Mobile money</option>
            </select></div>
          <div><label className="pc-field">Reference</label>
            <input className="pc-input" value={draft.reference || ''}
              onChange={(e) => setDraft(d => ({ ...d, reference: e.target.value }))} data-testid="pc-payment-reference" /></div>
        </div>
      ) },
    branch: { title: 'Add a branch', word: '', requireReason: false,
      description: 'Creates a child branch under this pharmacy.',
      run: () => act(() => api.post(`/platform/pharmacies/${tenantId}/branches`,
        { name: draft.name || '', town: draft.town || '', phone: draft.phone || '' }), 'Branch added'),
      body: (
        <div className="pc-row">
          {[['name', 'Branch name'], ['town', 'Town'], ['phone', 'Phone']].map(([k, label]) => (
            <div key={k}><label className="pc-field">{label}</label>
              <input className="pc-input" value={draft[k] || ''}
                onChange={(e) => setDraft(d => ({ ...d, [k]: e.target.value }))} data-testid={`pc-branch-${k}`} /></div>
          ))}
        </div>
      ) },
    deactivate: { title: `Deactivate ${draft.name || 'branch'}?`, word: 'DEACTIVATE',
      description: 'The branch stops being selectable. Its data is kept.',
      run: (reason) => act(() => api.post(`/platform/branches/${draft.branch_id}/deactivate`, { reason }), 'Branch deactivated') },
    grant: { title: `Grant ${draft.label || 'add-on'} credits`, word: '',
      description: 'Applied immediately. The reason is mandatory and audited.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/addons/grant`,
        { kind: draft.kind, units: Number(draft.units) || 0, reason }), 'Credits granted'),
      body: (<div><label className="pc-field">Units</label>
        <input className="pc-input" type="number" min={1} value={draft.units ?? (draft.bundle?.units || 1000)}
          onChange={(e) => setDraft(d => ({ ...d, units: e.target.value }))} data-testid="pc-grant-units" /></div>) },
    toggle: { title: `${draft.disabled ? 'Enable' : 'Disable'} ${draft.label || 'add-on'}?`, word: '',
      description: draft.disabled ? 'The pharmacy can use and buy this add-on again.'
                                  : 'The pharmacy can no longer spend or buy this add-on.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/addons/toggle`,
        { kind: draft.kind, disabled: !draft.disabled, reason }), 'Add-on updated') },
    slots: { title: 'Adjust paid locations', word: '',
      description: 'Positive adds an extra location slot, negative takes one back. '
        + 'Slots renew with the subscription; a new branch still needs approval.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/branch-slots`,
        { slots: Number(draft.slots) || 0, reason }), 'Paid locations updated'),
      body: (<div><label className="pc-field">Slots (+ or −)</label>
        <input className="pc-input" type="number" value={draft.slots ?? 1}
          onChange={(e) => setDraft(d => ({ ...d, slots: e.target.value }))} data-testid="pc-slots-count" /></div>) },
    adjust: { title: `Adjust ${draft.label || 'add-on'} balance`, word: '',
      description: 'Use a negative number to take credits back. Every adjustment is audited.',
      run: (reason) => act(() => api.post(`/platform/pharmacies/${tenantId}/addons/adjust`,
        { kind: draft.kind, units: Number(draft.units) || 0, reason }), 'Balance adjusted'),
      body: (<div><label className="pc-field">Units (+ or −)</label>
        <input className="pc-input" type="number" value={draft.units ?? 0}
          onChange={(e) => setDraft(d => ({ ...d, units: e.target.value }))} data-testid="pc-adjust-units" /></div>) },
    topup: { title: `Send a ${draft.label || 'add-on'} top-up link`, word: '',
      description: 'Raises a Paystack checkout for this pharmacy. Credits apply once it is paid.',
      run: (reason) => act(async () => {
        const { data: link } = await api.post(`/platform/pharmacies/${tenantId}/addons/topup-link`,
          { kind: draft.kind, reason, callback_url: `${window.location.origin}/standing` });
        setTopUp(link);
      }, 'Top-up link created') },
    kycupload: { title: 'Upload a KYC document', word: '', requireReason: false,
      description: 'Attach a document the pharmacy sent you. It lands in the review queue as pending.',
      run: () => act(async () => {
        const file = draft.file;
        if (!file) throw new Error('Choose a file');
        const body = new FormData();
        body.append('kind', draft.kind || 'business_registration');
        body.append('expiry_date', draft.expiry || '');
        body.append('file', file);
        await api.post(`/platform/pharmacies/${tenantId}/kyc`, body,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      }, 'Document uploaded'),
      body: (
        <div className="pc-row">
          <div><label className="pc-field">Document type</label>
            <select className="pc-select" value={draft.kind || 'business_registration'}
              onChange={(e) => setDraft(d => ({ ...d, kind: e.target.value }))} data-testid="pc-kyc-kind">
              {['business_registration', 'premises_licence', 'superintendent_licence', 'tin', 'director_ghana_card']
                .map(k => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
            </select></div>
          <div><label className="pc-field">Expiry (if a licence)</label>
            <input className="pc-input" type="date" value={draft.expiry || ''}
              onChange={(e) => setDraft(d => ({ ...d, expiry: e.target.value }))} data-testid="pc-kyc-expiry" /></div>
          <div><label className="pc-field">File (PDF or image)</label>
            <input className="pc-input" type="file" accept=".pdf,image/*"
              onChange={(e) => setDraft(d => ({ ...d, file: e.target.files?.[0] }))} data-testid="pc-kyc-file" /></div>
        </div>
      ) },
    branchdecision: { title: `${draft.decision === 'approved' ? 'Approve' : draft.decision === 'rejected' ? 'Reject' : 'Ask for information about'} ${draft.name || 'branch'}?`,
      word: draft.decision === 'rejected' ? 'REJECT' : '',
      requireReason: draft.decision !== 'approved',
      description: draft.decision === 'approved'
        ? 'The branch becomes selectable at the till and appears on the customer portal.'
        : 'The pharmacy sees this reason and can resubmit.',
      run: (reason) => act(() => api.post(`/platform/branches/${draft.branch_id}/decision`,
        { decision: draft.decision, reason }), 'Branch request updated') },
    impersonate: { title: `Open ${p.name} as support?`, word: 'SUPPORT',
      description: 'Starts a strictly read-only 15-minute session as this pharmacy’s administrator. The reason and session boundaries are audited.',
      run: startSupportView },
  };
  const active = dialog ? dialogs[dialog] : null;

  return (
    <div data-testid="pc-pharmacy-detail">
      <Link to="/platform/pharmacies" className="pc-muted" data-testid="pc-back-to-list">
        <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> All pharmacies
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginTop: '.5rem' }}>
        <div>
          <h1 className="pc-h1">{p.name}</h1>
          <p className="pc-sub">
            <span className="pc-mono">{p.tenant_id}</span> · {p.town || p.address || 'no town on file'} ·{' '}
            <Pill value={p.status} /> <Pill value={p.kyc_status} />
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          <button className="pc-btn pc-btn-support" onClick={() => open('impersonate')} data-testid={`impersonate-btn-${tenantId}`}><Eye className="h-3.5 w-3.5 inline mr-1" />Support view</button>
          <button className="pc-btn" onClick={() => open('plan', { tier: sub.tier, cycle: sub.cycle })} data-testid="pc-action-plan">Change plan</button>
          <button className="pc-btn" onClick={() => open('trial', { days: 14 })} data-testid="pc-action-trial">Trial</button>
          <button className="pc-btn" onClick={() => open('payment')} data-testid="pc-action-payment">Record payment</button>
          <button className="pc-btn" onClick={() => open('golive')} data-testid="pc-action-golive">Go live</button>
          <button className="pc-btn" onClick={() => open('cancel')} data-testid="pc-action-cancel">Cancel plan</button>
          {p.status === 'suspended'
            ? <button className="pc-btn pc-btn-primary" onClick={() => open('restore')} data-testid="pc-action-restore">Restore</button>
            : <button className="pc-btn pc-btn-danger" onClick={() => open('suspend')} data-testid="pc-action-suspend">Suspend</button>}
        </div>
      </div>

      <div className="pc-grid">
        <div className="pc-tile"><b style={{ textTransform: 'capitalize' }}>{sub.tier || '—'}</b><span>Plan {sub.cycle ? `· ${sub.cycle}` : ''}</span></div>
        <div className="pc-tile"><b>{when(sub.current_period_end)}</b><span>Renews</span></div>
        <div className="pc-tile"><b>{data.branches.length + 1}</b><span>Branches</span></div>
        <div className="pc-tile"><b>{data.users.length}</b><span>Users</span></div>
        <div className="pc-tile"><b>{when(p.trial_ends_at)}</b><span>Trial ends</span></div>
      </div>

      <div className="pc-card" style={{ marginBottom: '1rem' }}>
        <div className="pc-card-h"><h2>Add-on allowances</h2><span className="pc-muted">{data.allowances.year_month}</span></div>
        <div className="pc-scroll">
          <table className="pc-table">
            <thead><tr><th>Add-on</th><th className="pc-num">Used / included</th><th className="pc-num">Credits</th>
              <th className="pc-num">Remaining</th><th className="pc-num">Projected</th><th>Plan usage</th><th /></tr></thead>
            <tbody>
              {data.allowances.allowances.map(a => (
                <AllowanceRow key={a.kind} a={a}
                  onGrant={(row) => open('grant', { kind: row.kind, label: row.label, bundle: row.bundle, units: row.bundle.units })}
                  onAdjust={(row) => open('adjust', { kind: row.kind, label: row.label, units: 0 })}
                  onTopUp={(row) => open('topup', { kind: row.kind, label: row.label, bundle: row.bundle })}
                  onToggle={(row) => open('toggle', { kind: row.kind, label: row.label, disabled: row.disabled })} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pc-split">
        <div className="pc-card">
          <div className="pc-card-h"><h2>Invoices & transactions</h2></div>
          <div className="pc-scroll">
            <table className="pc-table">
              <thead><tr><th>Date</th><th>Reference</th><th>Channel</th><th className="pc-num">Amount</th><th>State</th></tr></thead>
              <tbody>
                {data.invoices.map(i => (
                  <tr key={i.invoice_id || i.reference} data-testid={`pc-invoice-${i.reference}`}>
                    <td>{when(i.created_at)}</td>
                    <td className="pc-mono">{i.reference}{i.out_of_band && ' · manual'}</td>
                    <td>{i.channel || '—'}</td>
                    <td className="pc-num">{money((i.amount_pesewas || 0) / 100)}</td>
                    <td><Pill value={i.status === 'paid' ? 'approved' : i.status} /></td>
                  </tr>
                ))}
                {data.invoices.length === 0 && <tr><td colSpan={5} className="pc-muted" style={{ textAlign: 'center', padding: '1.5rem' }}>No payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="pc-card" style={{ marginBottom: '1rem' }}>
            <div className="pc-card-h"><h2>Branches</h2>
              <button className="pc-btn" onClick={() => open('slots', { slots: 1 })}
                data-testid="pc-adjust-slots">Adjust paid locations</button>
              <button className="pc-btn" onClick={() => open('branch')} data-testid="pc-add-branch">Add branch</button></div>
            <table className="pc-table">
              <tbody>
                <tr><td>{p.name} <span className="pc-muted">(head)</span></td><td className="pc-num pc-muted">root</td></tr>
                {data.branches.map(b => (
                  <tr key={b.tenant_id} data-testid={`pc-branch-${b.tenant_id}`}>
                    <td>{b.name}
                      {b.is_active === false && <span className="pc-pill pc-pill-suspended" style={{ marginLeft: 6 }}>off</span>}
                      <div className="pc-muted">{b.town || b.address || '—'}</div>
                      <Pill value={b.approval_status || 'approved'} />
                      {b.approval_reason && <div className="pc-muted">“{b.approval_reason}”</div>}
                    </td>
                    <td className="pc-num" style={{ whiteSpace: 'nowrap' }}>
                      {(b.approval_status || 'approved') !== 'approved' && (
                        <>
                          <button className="pc-btn pc-btn-primary"
                            onClick={() => open('branchdecision', { branch_id: b.tenant_id, name: b.name, decision: 'approved' })}
                            data-testid={`pc-approve-branch-${b.tenant_id}`}>Approve</button>{' '}
                          <button className="pc-btn"
                            onClick={() => open('branchdecision', { branch_id: b.tenant_id, name: b.name, decision: 'info_requested' })}
                            data-testid={`pc-info-branch-${b.tenant_id}`}>Ask info</button>{' '}
                        </>
                      )}
                      {b.is_active !== false && (
                        <button className="pc-btn pc-btn-danger"
                          onClick={() => open('deactivate', { branch_id: b.tenant_id, name: b.name })}
                          data-testid={`pc-deactivate-${b.tenant_id}`}>Deactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pc-card">
            <div className="pc-card-h"><h2>KYC documents</h2>
              <span>
                <button className="pc-btn" onClick={() => open('kycupload', { kind: 'business_registration' })}
                  data-testid="pc-upload-kyc">Upload</button>{' '}
                <Link className="pc-btn" to="/platform/kyc">Review queue</Link>
              </span></div>
            <table className="pc-table">
              <tbody>
                {data.kyc_documents.map(d => (
                  <tr key={d.doc_id} data-testid={`pc-kycdoc-${d.doc_id}`}>
                    <td>{d.kind.replace(/_/g, ' ')}<div className="pc-muted">{d.expiry_date ? `expires ${d.expiry_date}` : 'no expiry'}</div></td>
                    <td className="pc-num"><Pill value={d.status} /></td>
                  </tr>
                ))}
                {data.kyc_documents.length === 0 && <tr><td className="pc-muted" style={{ padding: '1.2rem' }}>Nothing uploaded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pc-card" style={{ marginTop: '1rem' }}>
        <div className="pc-card-h"><h2>Audit trail</h2></div>
        <table className="pc-table">
          <tbody>
            {data.audit.map(a => (
              <tr key={a.audit_id}>
                <td className="pc-mono">{when(a.created_at)}</td>
                <td>{a.action}</td>
                <td className="pc-muted">{a.detail}{a.reason ? ` — “${a.reason}”` : ''}</td>
                <td className="pc-muted pc-num">{a.actor_name}</td>
              </tr>
            ))}
            {data.audit.length === 0 && <tr><td className="pc-muted" style={{ padding: '1.2rem' }}>No actions recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {topUpLink && (
        <div className="pc-card" style={{ marginTop: '1rem' }} data-testid="pc-topup-result">
          <div className="pc-card-h"><h2>Top-up payment link</h2>
            <button className="pc-btn" onClick={() => setTopUp(null)} data-testid="pc-topup-dismiss">Dismiss</button></div>
          <div style={{ padding: '.9rem' }}>
            <p className="pc-sub">{topUpLink.bundle.units.toLocaleString()} {topUpLink.bundle.label} · {money(topUpLink.bundle.price_ghs)} · billed to {topUpLink.billing_email}</p>
            <p className="pc-mono" style={{ wordBreak: 'break-all' }} data-testid="pc-topup-url">{topUpLink.payment_url}</p>
          </div>
        </div>
      )}

      {active && (
        <ConfirmAction
          open title={active.title} description={active.description}
          confirmWord={active.word} requireReason={active.requireReason !== false}
          busy={busy} onCancel={() => setDialog(null)} onConfirm={active.run}
        >
          {active.body}
        </ConfirmAction>
      )}
    </div>
  );
};
